// src/services/api/userFeedback.ts
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  updateDoc, 
  doc,
  Timestamp,
  startAfter,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FeedbackResolvedUpdateData } from '../../types/feedback';
import { logDev, logInfo } from '../../utils/logger';

// 用戶回饋資料結構
export interface UserFeedback {
  id: string;
  userId: string;
  userEmail: string;
  content: string;
  isRead: boolean;
  isResolved: boolean;
  createdAt: Timestamp;
  context: {
    currentPage: string;
    userAgent: string;
  };
  adminNotes?: string;
  resolvedAt?: Timestamp;
}

// 提交回饋的請求參數
export interface SubmitFeedbackRequest {
  userId: string;
  userEmail: string;
  content: string;
  context: {
    currentPage: string;
    userAgent: string;
  };
}

// Firestore collection 名稱
const COLLECTION_NAME = 'userFeedback';

/**
 * 檢查用戶是否在限制時間內已提交過回饋（防濫用）
 */
export const checkRateLimit = async (userId: string, limitMinutes: number = 5): Promise<boolean> => {
  try {
    // 開發環境跳過頻率限制，方便測試
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    if (isDevelopment) {
      logDev('Development mode: skipping rate limit check');
      return true;
    }

    const limitTime = new Date();
    limitTime.setMinutes(limitTime.getMinutes() - limitMinutes);
    
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('createdAt', '>', Timestamp.fromDate(limitTime)),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.empty; // true 表示可以提交，false 表示需要等待
  } catch (error) {
    console.error('Check rate limit failed:', error);
    return true; // 發生錯誤時允許提交
  }
};

/**
 * 提交用戶回饋
 */
export const submitUserFeedback = async (request: SubmitFeedbackRequest): Promise<string> => {
  try {
    // 檢查頻率限制
    const canSubmit = await checkRateLimit(request.userId);
    if (!canSubmit) {
      throw new Error('Please wait 5 minutes before sending another feedback');
    }

    // 準備回饋資料
    const feedbackData = {
      userId: request.userId,
      userEmail: request.userEmail,
      content: request.content,
      isRead: false,
      isResolved: false,
      createdAt: Timestamp.now(),
      context: request.context
    };

    // 新增到 Firestore
    const docRef = await addDoc(collection(db, COLLECTION_NAME), feedbackData);
    
    logInfo('User feedback submitted successfully', { feedbackId: docRef.id });
    return docRef.id;
    
  } catch (error) {
    console.error('Submit user feedback failed:', error);
    throw error;
  }
};

/**
 * 獲取用戶的回饋歷史
 */
export const getUserFeedbackHistory = async (userId: string): Promise<UserFeedback[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const feedbacks: UserFeedback[] = [];
    
    querySnapshot.forEach((doc) => {
      feedbacks.push({
        id: doc.id,
        ...doc.data()
      } as UserFeedback);
    });
    
    return feedbacks;
  } catch (error) {
    console.error('Get user feedback history failed:', error);
    throw error;
  }
};

// === 管理員功能 ===

/**
 * 獲取所有回饋（分頁）
 */
export interface GetAllFeedbackOptions {
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot;
  searchUserId?: string;
  showUnreadOnly?: boolean;
  showUnresolvedOnly?: boolean;
}

export interface GetAllFeedbackResult {
  feedbacks: UserFeedback[];
  lastDoc?: QueryDocumentSnapshot;
  hasMore: boolean;
}

export const getAllFeedback = async (options: GetAllFeedbackOptions = {}): Promise<GetAllFeedbackResult> => {
  try {
    const {
      pageSize = 20,
      lastDoc,
      searchUserId,
      showUnreadOnly = false,
      showUnresolvedOnly = false
    } = options;

    let q = query(collection(db, COLLECTION_NAME));

    // 搜尋特定用戶
    if (searchUserId) {
      q = query(q, where('userId', '==', searchUserId));
    }

    // 只顯示未讀
    if (showUnreadOnly) {
      q = query(q, where('isRead', '==', false));
    }

    // 只顯示未解決
    if (showUnresolvedOnly) {
      q = query(q, where('isResolved', '==', false));
    }

    // 排序和分頁
    q = query(q, orderBy('createdAt', 'desc'), limit(pageSize));
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(q);
    const feedbacks: UserFeedback[] = [];
    
    querySnapshot.forEach((doc) => {
      feedbacks.push({
        id: doc.id,
        ...doc.data()
      } as UserFeedback);
    });

    const lastDocument = querySnapshot.docs[querySnapshot.docs.length - 1];
    const hasMore = querySnapshot.docs.length === pageSize;

    return {
      feedbacks,
      lastDoc: lastDocument,
      hasMore
    };
  } catch (error) {
    console.error('Get all feedback failed:', error);
    throw error;
  }
};

/**
 * 標記回饋為已讀
 */
export const markFeedbackAsRead = async (feedbackId: string): Promise<void> => {
  try {
    const feedbackRef = doc(db, COLLECTION_NAME, feedbackId);
    await updateDoc(feedbackRef, {
      isRead: true
    });
  } catch (error) {
    console.error('Mark feedback as read failed:', error);
    throw error;
  }
};

/**
 * 標記回饋為已解決
 */
export const markFeedbackAsResolved = async (feedbackId: string, adminNotes?: string): Promise<void> => {
  try {
    const feedbackRef = doc(db, COLLECTION_NAME, feedbackId);
    const updateData: FeedbackResolvedUpdateData = {
      isResolved: true,
      resolvedAt: Timestamp.now(),
      ...(adminNotes ? { adminNotes } : {})
    };
    
    await updateDoc(feedbackRef, updateData as any);
  } catch (error) {
    console.error('Mark feedback as resolved failed:', error);
    throw error;
  }
};

/**
 * 批量標記為已讀
 */
export const batchMarkAsRead = async (feedbackIds: string[]): Promise<void> => {
  try {
    const promises = feedbackIds.map(id => markFeedbackAsRead(id));
    await Promise.all(promises);
  } catch (error) {
    console.error('Batch mark as read failed:', error);
    throw error;
  }
};

/**
 * 獲取未讀回饋數量
 */
export const getUnreadFeedbackCount = async (): Promise<number> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('isRead', '==', false)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Get unread feedback count failed:', error);
    return 0;
  }
};